import React, { useState } from 'react';
import { 
  Eye, 
  Filter, 
  Brain, 
  Lightbulb, 
  CheckCircle, 
  Heart, 
  Zap,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Download,
  Maximize2,
  Minimize2
} from 'lucide-react';

interface LadderOfInferenceProps {
  format?: 'slide' | 'web' | 'mobile' | 'print' | 'field-card' | 'poster';
  variant?: 'vertical' | 'horizontal' | 'circular' | 'journey';
  contentDensity?: 'minimal' | 'standard' | 'detailed';
  interactive?: boolean;
  showReflection?: boolean;
}

export const LadderOfInference: React.FC<LadderOfInferenceProps> = ({
  format = 'web',
  variant = 'vertical',
  contentDensity = 'standard',
  interactive = true,
  showReflection = true
}) => {
  const [activeRung, setActiveRung] = useState<number | null>(null);
  const [showDescending, setShowDescending] = useState(false);

  const rungs = [
    {
      id: 1,
      title: 'Observable Data',
      description: 'Raw facts and events that can be directly observed',
      icon: <Eye className="h-5 w-5" />,
      color: 'primary',
      examples: ['Sales numbers', 'Customer feedback', 'Meeting attendance'],
      coachingQuestions: ['What exactly did you observe?', 'What are the facts?'],
      minimalText: 'Facts & Events'
    },
    {
      id: 2,
      title: 'Selected Data',
      description: 'The subset of observable data we choose to focus on',
      icon: <Filter className="h-5 w-5" />,
      color: 'sage',
      examples: ['Recent sales decline', 'Negative reviews', 'Low participation'],
      coachingQuestions: ['What did you choose to focus on?', 'What did you ignore?'],
      minimalText: 'What We Notice'
    },
    {
      id: 3,
      title: 'Interpreted Meaning',
      description: 'The meaning we assign to the selected data',
      icon: <Brain className="h-5 w-5" />,
      color: 'sage',
      examples: ['Team is disengaged', 'Product has issues', 'Leadership is failing'],
      coachingQuestions: ['What does this mean to you?', 'How do you interpret this?'],
      minimalText: 'What It Means'
    },
    {
      id: 4,
      title: 'Assumptions Made',
      description: 'Beliefs we hold about the situation, often unstated',
      icon: <Lightbulb className="h-5 w-5" />,
      color: 'amber',
      examples: ['People are lazy', 'Process is broken', 'No one cares'],
      coachingQuestions: ['What assumptions are you making?', 'What do you believe to be true?'],
      minimalText: 'Assumptions'
    },
    {
      id: 5,
      title: 'Conclusions Drawn',
      description: 'The logical conclusions we reach based on our assumptions',
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'amber',
      examples: ['Need to fire people', 'Product must be redesigned', 'Leadership change required'],
      coachingQuestions: ['What conclusions have you reached?', 'What do you think should happen?'],
      minimalText: 'Conclusions'
    },
    {
      id: 6,
      title: 'Beliefs Formed',
      description: 'The beliefs we develop about people, situations, or ourselves',
      icon: <Heart className="h-5 w-5" />,
      color: 'luminous-coral',
      examples: ['Team is incompetent', 'I am a bad leader', 'This will never work'],
      coachingQuestions: ['What beliefs have you formed?', 'How do you see yourself/others?'],
      minimalText: 'Beliefs'
    },
    {
      id: 7,
      title: 'Actions Taken',
      description: 'The behaviors and decisions we make based on our beliefs',
      icon: <Zap className="h-5 w-5" />,
      color: 'luminous-coral',
      examples: ['Micromanage team', 'Avoid difficult conversations', 'Give up on project'],
      coachingQuestions: ['What actions will you take?', 'How will you behave?'],
      minimalText: 'Actions'
    }
  ];

  const getFormatStyles = () => {
    switch (format) {
      case 'slide':
        return {
          container: 'max-w-4xl mx-auto',
          rungSize: 'h-14',
          textSize: 'text-xs',
          spacing: 'space-y-2',
          iconSize: 'h-4 w-4',
          padding: 'p-2'
        };
      case 'web':
        return {
          container: 'max-w-6xl mx-auto',
          rungSize: 'h-20',
          textSize: 'text-base',
          spacing: 'space-y-3',
          iconSize: 'h-5 w-5',
          padding: 'p-4'
        };
      case 'mobile':
        return {
          container: 'max-w-sm mx-auto',
          rungSize: 'h-10',
          textSize: 'text-xs',
          spacing: 'space-y-1',
          iconSize: 'h-3 w-3',
          padding: 'p-2'
        };
      case 'print':
        return {
          container: 'max-w-5xl mx-auto',
          rungSize: 'h-16',
          textSize: 'text-sm',
          spacing: 'space-y-2',
          iconSize: 'h-4 w-4',
          padding: 'p-3'
        };
      case 'field-card':
        return {
          container: 'max-w-xs mx-auto',
          rungSize: 'h-8',
          textSize: 'text-xs',
          spacing: 'space-y-1',
          iconSize: 'h-3 w-3',
          padding: 'p-1'
        };
      case 'poster':
        return {
          container: 'mx-auto',
          rungSize: 'h-20',
          textSize: 'text-base',
          spacing: 'space-y-3',
          iconSize: 'h-6 w-6',
          padding: 'p-4'
        };
      default:
        return {
          container: 'max-w-6xl mx-auto',
          rungSize: 'h-20',
          textSize: 'text-base',
          spacing: 'space-y-3',
          iconSize: 'h-5 w-5',
          padding: 'p-4'
        };
    }
  };

  const getContentLevel = (rung: any) => {
    switch (contentDensity) {
      case 'minimal':
        return rung.minimalText;
      case 'standard':
        return rung.title;
      case 'detailed':
        return (
          <div>
            <div className="font-semibold">{rung.title}</div>
            <div className="text-sm opacity-75">{rung.description}</div>
          </div>
        );
      default:
        return rung.title;
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'bg-primary-100 border-primary-300 text-primary-800';
      case 'sage':
        return 'bg-sage-100 border-sage-300 text-sage-800';
      case 'amber':
        return 'bg-amber-100 border-amber-300 text-amber-800';
      case 'luminous-coral':
        return 'bg-luminous-coral-100 border-luminous-coral-300 text-luminous-coral-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const formatStyles = getFormatStyles();

  const renderVerticalLadder = () => (
    <div className={`${formatStyles.container} ${formatStyles.spacing}`}>
      {format !== 'field-card' && (
        <div className="text-center mb-4">
          <h2 className={`${format === 'slide' ? 'text-lg' : 'text-2xl'} font-bold text-charcoal-900 mb-1`}>
            The Ladder of Inference
          </h2>
          <p className={`${format === 'slide' ? 'text-xs' : 'text-sm'} text-charcoal-600`}>
            How we move from observable data to conclusions and actions
          </p>
        </div>
      )}
      
      <div className="relative">
        {/* Ladder Structure */}
        <div className={`absolute ${format === 'field-card' ? 'left-7' : 'left-8'} top-0 bottom-0 w-1 bg-charcoal-300`}></div>
        
        {rungs.map((rung, index) => (
          <div key={rung.id} className={`relative flex items-center ${format === 'field-card' ? 'mb-1' : 'mb-3'}`}>
            {/* Rung Number */}
            <div className={`absolute left-6 ${format === 'field-card' ? 'w-3 h-3 text-xs' : 'w-4 h-4 text-xs'} bg-white border-2 border-charcoal-600 rounded-full flex items-center justify-center font-bold text-charcoal-600 z-10`}>
              {rung.id}
            </div>
            
            {/* Rung Content */}
            <div 
              className={`${format === 'field-card' ? 'ml-12' : 'ml-16'} ${formatStyles.rungSize} w-full rounded-lg border-2 ${getColorClasses(rung.color)} ${formatStyles.padding} flex items-center gap-2 cursor-pointer transition-all hover:shadow-md ${
                activeRung === rung.id ? 'ring-2 ring-charcoal-400' : ''
              }`}
              onClick={() => interactive && setActiveRung(activeRung === rung.id ? null : rung.id)}
            >
              <div className="flex-shrink-0">
                <div className={formatStyles.iconSize}>
                  {rung.icon}
                </div>
              </div>
              <div className="flex-1">
                <div className={`${formatStyles.textSize} font-medium`}>
                  {getContentLevel(rung)}
                </div>
              </div>
              {interactive && (
                <div className="flex-shrink-0">
                  <ArrowUp className="h-3 w-3 opacity-50" />
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Reflection Path */}
        {showReflection && (
          <div className="absolute right-0 top-0 bottom-0 w-1 bg-dashed border-2 border-dashed border-amber-400 opacity-50"></div>
        )}
      </div>
      
      {/* Active Rung Details */}
      {interactive && activeRung && (
        <div className="mt-6 p-6 bg-white rounded-lg border-2 border-gray-200 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            {rungs[activeRung - 1].icon}
            <h3 className="text-xl font-bold text-charcoal-900">{rungs[activeRung - 1].title}</h3>
          </div>
          <p className="text-charcoal-700 mb-4">{rungs[activeRung - 1].description}</p>
          
          {contentDensity === 'detailed' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-2">Examples:</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  {rungs[activeRung - 1].examples.map((example, index) => (
                    <li key={index}>{example}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-2">Coaching Questions:</h4>
                <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-1">
                  {rungs[activeRung - 1].coachingQuestions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Reflection Section */}
      {showReflection && (
        <div className="mt-8 p-6 bg-gradient-to-r from-amber-50 to-primary-50 rounded-lg border-2 border-amber-200">
          <div className="flex items-center gap-3 mb-4">
            <RotateCcw className="h-6 w-6 text-amber-600" />
            <h3 className="text-xl font-bold text-charcoal-900">Reflection & Descent</h3>
          </div>
          <p className="text-charcoal-700 mb-4">
            To improve reasoning and reduce conflict, we can "climb down" the ladder by questioning our assumptions and examining our data selection.
          </p>
          <div className="flex items-center gap-2 text-amber-700">
            <ArrowDown className="h-4 w-4" />
            <span className="text-sm font-medium">Question assumptions → Re-examine data → Adjust conclusions</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderHorizontalFlow = () => (
    <div className={`${formatStyles.container}`}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Reasoning Process Flow</h2>
        <p className="text-charcoal-600">From observation to action: understanding our mental journey</p>
      </div>
      
      <div className="flex items-center justify-between">
        {rungs.map((rung, index) => (
          <div key={rung.id} className="flex flex-col items-center">
            <div 
              className={`w-24 ${formatStyles.rungSize} rounded-lg border-2 ${getColorClasses(rung.color)} p-3 flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-md ${
                activeRung === rung.id ? 'ring-2 ring-charcoal-400' : ''
              }`}
              onClick={() => interactive && setActiveRung(activeRung === rung.id ? null : rung.id)}
            >
              <div className="mb-2">{rung.icon}</div>
              <div className={`${formatStyles.textSize} font-medium text-center`}>
                {contentDensity === 'minimal' ? rung.minimalText : rung.title}
              </div>
            </div>
            {index < rungs.length - 1 && (
              <ArrowUp className="h-4 w-4 text-charcoal-400 mt-2 rotate-90" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderCircularFlow = () => (
    <div className={`${formatStyles.container}`}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Reflective Learning Cycle</h2>
        <p className="text-charcoal-600">Continuous improvement through questioning and reflection</p>
      </div>
      
      <div className="relative w-96 h-96 mx-auto">
        {rungs.map((rung, index) => {
          const angle = (index * 360) / rungs.length;
          const x = 150 + 120 * Math.cos((angle - 90) * Math.PI / 180);
          const y = 150 + 120 * Math.sin((angle - 90) * Math.PI / 180);
          
          return (
            <div
              key={rung.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: x, top: y }}
            >
              <div 
                className={`w-16 h-16 rounded-full border-2 ${getColorClasses(rung.color)} flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-md ${
                  activeRung === rung.id ? 'ring-2 ring-charcoal-400' : ''
                }`}
                onClick={() => interactive && setActiveRung(activeRung === rung.id ? null : rung.id)}
              >
                <div className="text-xs">{rung.icon}</div>
                <div className="text-xs font-medium text-center">
                  {contentDensity === 'minimal' ? rung.id : rung.title.split(' ')[0]}
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Center circle */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white border-2 border-charcoal-300 rounded-full flex items-center justify-center">
          <RotateCcw className="h-8 w-8 text-charcoal-600" />
        </div>
      </div>
    </div>
  );

  const renderJourneyMap = () => (
    <div className={`${formatStyles.container}`}>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-2">Personal Reasoning Journey</h2>
        <p className="text-charcoal-600">Mapping your path from observation to action</p>
      </div>
      
      <div className="relative">
        {/* Journey Path */}
        <svg className="w-full h-32 absolute top-0 left-0" viewBox="0 0 800 120">
          <path
            d="M 50 60 Q 200 20 350 60 Q 500 100 650 60 Q 750 40 800 60"
            stroke="#6B7280"
            strokeWidth="3"
            fill="none"
            strokeDasharray="5,5"
          />
        </svg>
        
        <div className="flex justify-between items-center">
          {rungs.map((rung, index) => (
            <div key={rung.id} className="flex flex-col items-center">
              <div 
                className={`w-16 h-16 rounded-full border-2 ${getColorClasses(rung.color)} flex flex-col items-center justify-center cursor-pointer transition-all hover:shadow-md ${
                  activeRung === rung.id ? 'ring-2 ring-charcoal-400' : ''
                }`}
                onClick={() => interactive && setActiveRung(activeRung === rung.id ? null : rung.id)}
              >
                <div className="text-xs">{rung.icon}</div>
                <div className="text-xs font-medium text-center">
                  {contentDensity === 'minimal' ? rung.id : rung.title.split(' ')[0]}
                </div>
              </div>
              <div className={`${formatStyles.textSize} font-medium text-center mt-2 max-w-20`}>
                {contentDensity === 'minimal' ? rung.minimalText : rung.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderLadder = () => {
    switch (variant) {
      case 'horizontal':
        return renderHorizontalFlow();
      case 'circular':
        return renderCircularFlow();
      case 'journey':
        return renderJourneyMap();
      default:
        return renderVerticalLadder();
    }
  };

  return (
    <div className="p-6">
      {renderLadder()}
      
      {/* Format Controls (for demonstration) */}
      {format === 'web' && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-wrap gap-2 justify-center">
            <span className="text-sm text-charcoal-600">Format:</span>
            {['slide', 'web', 'mobile', 'print', 'field-card', 'poster'].map((fmt) => (
              <button
                key={fmt}
                className={`px-3 py-1 rounded text-xs font-medium ${
                  format === fmt ? 'bg-primary-600 text-white' : 'bg-white text-charcoal-600 border border-gray-300'
                }`}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
